import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, Trash2, FileQuestion, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function StaffTrash() {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emptying, setEmptying] = useState(false);

  const load = () =>
    api.get("/works/deleted")
      .then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  useEffect(() => {
    load();
  }, []);

  const restore = async (w) => {
    setBusyId(w.id);
    try {
      await api.post(`/works/${w.id}/restore`);
      toast.success("Travail restauré : il est de nouveau visible dans l'historique de son auteur.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const emptyAll = async () => {
    setEmptying(true);
    try {
      const r = await api.delete("/works/deleted/empty");
      toast.success(`${r.data.deleted_count} travail(s) supprimé(s) définitivement.`);
      setConfirmOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEmptying(false);
    }
  };

  return (
    <div data-testid="admin-trash-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-display text-xl font-bold text-korban-navy">
            <Trash2 size={19} className="text-korban-blue" /> Corbeille des travaux supprimés
          </h2>
          <p className="mt-2 max-w-xl text-sm text-slate-500">
            Aucun travail n'est détruit : vous pouvez consulter les suppressions (avec leur motif) et restaurer un
            travail à tout moment. Les travaux supprimés depuis plus de 90 jours sont détruits automatiquement.
          </p>
        </div>
        {items && items.length > 0 && (
          <button onClick={() => setConfirmOpen(true)} data-testid="trash-empty-all-button"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2 text-xs font-bold text-red-600 transition-all hover:-translate-y-0.5 hover:bg-red-100">
            <Trash2 size={13} /> Vider la corbeille
          </button>
        )}
      </div>

      {!items ? (
        <p className="mt-4 flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
      ) : items.length === 0 ? (
        <p className="mt-4 rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="trash-empty">
          La corbeille est vide.
        </p>
      ) : (
        <div className="mt-5 space-y-3">
          {items.map((w) => (
            <article key={w.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`trash-card-${w.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[0.7rem] font-bold uppercase tracking-[0.14em] text-korban-blue">{w.company}</p>
                  <h3 className="mt-0.5 font-display text-base font-semibold leading-snug text-korban-navy">{w.title}</h3>
                </div>
                <span className="shrink-0 rounded bg-red-50 px-2 py-0.5 text-xs font-bold text-red-600">Supprimé</span>
              </div>
              {w.delete_reason ? (
                <p className="mt-2 rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                  <b className="text-korban-navy">Motif :</b> {w.delete_reason}
                </p>
              ) : (
                <p className="mt-2 text-xs italic text-slate-400"><FileQuestion size={12} className="inline" /> Supprimé par le gérant sans motif.</p>
              )}
              <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-slate-500">
                  {w.author_name} · {(w.work_date || "").slice(0, 10)} · supprimé par {w.deleted_by_name} le {(w.deleted_at || "").slice(0, 16).replace("T", " ")}
                </p>
                <button onClick={() => restore(w)} disabled={busyId === w.id} data-testid={`trash-restore-${w.id}`}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-korban-blue px-3.5 py-2 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:brightness-110 disabled:opacity-50">
                  {busyId === w.id ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />} Restaurer
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="border-2 border-red-200 sm:rounded-2xl" data-testid="trash-empty-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle size={20} /> Vider définitivement la corbeille ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left text-sm text-slate-600">
                <p>
                  Vous êtes sur le point de détruire <b className="text-red-600" data-testid="trash-empty-count">{items ? items.length : 0}</b> travail(s)
                  supprimé(s), avec leurs motifs de suppression et leurs pièces jointes.
                </p>
                <p className="rounded-lg border border-red-200 bg-red-50 p-3 font-semibold text-red-700">
                  Attention : cette action est irréversible. Les travaux détruits ne pourront plus être restaurés ni
                  retrouvés.
                </p>
                <p>Si vous souhaitez conserver un travail, restaurez-le avant de vider la corbeille.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-empty-cancel" disabled={emptying}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={emptyAll} disabled={emptying} data-testid="trash-empty-confirm"
              className="bg-red-600 text-white hover:bg-red-700">
              {emptying ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Oui, tout supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
